import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import AutoGrid from './layout/AutoGrid';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { getCategoryUsageByYear } from '../services/homeExpensesApi';
import { useExpensesYear } from '../context/ExpensesYearContext';
import { formatEuro, formatNumber } from '../utils/currency';
import { CategoryUsageDto } from '../types/homeExpenses';

interface CategoryUsageChartProps {
  title?: string;
  height?: number;
}

const CategoryUsageChart: React.FC<CategoryUsageChartProps> = ({ 
  title = "Análise de utilização das categorias", 
  height = 400 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { year: selectedYear } = useExpensesYear();
  const [categoryData, setCategoryData] = useState<CategoryUsageDto[]>([]);

  // Removed local year list; using global unified year

  const fetchCategoryData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getCategoryUsageByYear(selectedYear);
      setCategoryData(data);
      
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar os dados de utilização das categorias');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchCategoryData();
  }, [fetchCategoryData]);

  const formatCurrency = formatEuro;

  const renderCustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {data.name}
          </Typography>
          <Typography variant="body2">
            Valor: {formatCurrency(data.value)}
          </Typography>
          <Typography variant="body2">
            Percentagem: {formatNumber(data.percentage, 1)}%
          </Typography>
          <Typography variant="body2">
            Despesas: {data.expenseCount}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const renderLabel = ({ name, percentage }: any) => {
    return percentage > 5 ? `${formatNumber(percentage, 0)}%` : '';
  };

  // Group data by category
  const categoriesData = categoryData.reduce((acc, item) => {
    if (!acc[item.categoryName]) {
      acc[item.categoryName] = {
        categoryName: item.categoryName,
        categoryColor: item.categoryColor,
        isIncomeCategory: item.isIncomeCategory,
        groups: []
      };
    }
    acc[item.categoryName].groups.push({
      name: item.groupName,
      value: item.totalAmount,
      percentage: item.percentage,
      color: item.groupColor,
      expenseCount: item.expenseCount
    });
    return acc;
  }, {} as Record<string, any>);

  const categories = Object.values(categoriesData);

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          A carregar dados de utilização das categorias...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
        {title} – {selectedYear}
      </Typography>

      {categories.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Sem dados de categorias para {selectedYear}
          </Typography>
        </Paper>
      ) : (
        <AutoGrid min={300} gap={3}>
          {categories.map((category, index) => (
            <Paper 
              key={category.categoryName}
              sx={{ p: 2, backgroundColor: '#f5f5f5', height: '100%' }}
            >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    textAlign: 'center', 
                    mb: 2, 
                    fontWeight: 'bold',
                    color: category.isIncomeCategory ? '#4caf50' : '#333'
                  }}
                >
                  {category.categoryName}
                </Typography>
                <Box sx={{ height: height }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={category.groups}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderLabel}
                        outerRadius={Math.min(height * 0.35, 120)}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {category.groups.map((entry: any, groupIndex: number) => (
                          <Cell key={`cell-${index}-${groupIndex}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={renderCustomTooltip} />
                      <Legend 
                        wrapperStyle={{ fontSize: '12px' }}
                        formatter={(value: string, entry: any) => (
                          <span style={{ color: entry.color, fontSize: '11px' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, fontWeight: 'bold' }}>
                  Total: {formatCurrency(category.groups.reduce((sum: number, item: any) => sum + item.value, 0))}
                </Typography>
              </Paper>
            ))}
        </AutoGrid>
      )}
    </Box>
  );
};

export default CategoryUsageChart;
